import { GameEngine, GameEvent, clone, err } from "../engine.js";
import { RngState, nextFloat } from "../rng.js";
import { Body, Table, clampNum, simulate } from "../physics.js";

/**
 * 8-ball pool — Samra rules v1 (docs/games/billiards.md). 2 players.
 * Fouls (cue ball potted, no contact, wrong first contact, no rail/pot after
 * contact) give the opponent ball-in-hand. Pot the 8 after clearing your
 * group to win; potting it early or on a foul loses.
 */

export const POOL_TABLE: Table = {
  w: 1000,
  h: 500,
  pockets: [
    { x: 0, y: 0, r: 26 }, { x: 500, y: -6, r: 24 }, { x: 1000, y: 0, r: 26 },
    { x: 0, y: 500, r: 26 }, { x: 500, y: 506, r: 24 }, { x: 1000, y: 500, r: 26 },
  ],
  friction: 380,
  cushion: 0.75,
  restitution: 0.95,
};
export const BALL_R = 11;
const MAX_SPEED = 2600;
const HEAD_X = 250;

export type Group = "solid" | "stripe";
export interface PoolBall { id: number; x: number; y: number; pocketed: boolean }

export interface PoolState {
  n: 2;
  balls: PoolBall[];
  turn: number;
  groups: (Group | null)[];
  ballInHand: boolean;
  broken: boolean;
  over: boolean;
  winner: number | null;
  shots: number;
  rng: RngState;
  lastFoul: string | null;
}

export type PoolMove =
  | { type: "shoot"; angle: number; power: number }
  | { type: "place"; x: number; y: number };

export type PoolView = Omit<PoolState, "rng">;

const groupOf = (id: number): Group | "eight" | "cue" =>
  id === 0 ? "cue" : id === 8 ? "eight" : id < 8 ? "solid" : "stripe";

function rack(rng: RngState): PoolBall[] {
  const balls: PoolBall[] = [{ id: 0, x: HEAD_X, y: 250, pocketed: false }];
  const order = [1, 9, 2, 10, 8, 3, 11, 7, 14, 4, 5, 13, 15, 6, 12];
  // small random jitter of non-8 balls keeps every break different
  const rest = order.filter((x) => x !== 8);
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(nextFloat(rng) * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  const ids = [...rest.slice(0, 4), 8, ...rest.slice(4)];
  let k = 0;
  const d = BALL_R * 2 + 0.5;
  for (let row = 0; row < 5; row++) {
    for (let c = 0; c <= row; c++) {
      balls.push({
        id: ids[k++],
        x: 740 + row * d * Math.cos(Math.PI / 6),
        y: 250 + (c - row / 2) * d,
        pocketed: false,
      });
    }
  }
  return balls;
}

function remaining(s: PoolState, g: Group) {
  return s.balls.filter((b) => !b.pocketed && groupOf(b.id) === g).length;
}

function freeSpot(s: PoolState, x: number, y: number) {
  return s.balls.every((b) => b.id === 0 || b.pocketed || Math.hypot(b.x - x, b.y - y) > BALL_R * 2);
}

export const billiards: GameEngine<PoolState, PoolMove, PoolView, {}> = {
  id: "billiards",
  minPlayers: 2,
  maxPlayers: 2,
  playerCounts: [2],
  defaultOptions: {},

  init(_n, seed) {
    const rng = { s: seed | 0 };
    return {
      n: 2, balls: rack(rng), turn: 0, groups: [null, null], ballInHand: false,
      broken: false, over: false, winner: null, shots: 0, rng, lastFoul: null,
    };
  },

  currentSeat: (s) => (s.over ? null : s.turn),

  applyMove(state, seat, move) {
    if (state.over) return err("game_over");
    if (seat !== state.turn) return err("not_your_turn");
    const s = clone(state);
    const ev: GameEvent[] = [];

    if (move?.type === "place") {
      if (!s.ballInHand) return err("no_ball_in_hand");
      const x = clampNum(move.x, BALL_R, POOL_TABLE.w - BALL_R);
      const y = clampNum(move.y, BALL_R, POOL_TABLE.h - BALL_R);
      if (x == null || y == null) return err("bad_position");
      if (!s.broken && x > HEAD_X) return err("behind_head_string");
      if (!freeSpot(s, x, y)) return err("spot_taken");
      const cue = s.balls.find((b) => b.id === 0)!;
      Object.assign(cue, { x, y, pocketed: false });
      s.ballInHand = false;
      ev.push({ type: "placed", seat, x, y });
      return { ok: true, state: s, events: ev };
    }

    if (move?.type !== "shoot") return err("unknown_move");
    if (s.ballInHand) return err("place_cue_ball_first");
    const angle = clampNum(move.angle, -Math.PI * 4, Math.PI * 4);
    const power = clampNum(move.power, 0.05, 1);
    if (angle == null || power == null) return err("bad_shot");

    const bodies: Body[] = s.balls.filter((b) => !b.pocketed).map((b) => ({
      id: b.id, x: b.x, y: b.y, vx: 0, vy: 0, r: BALL_R, m: 1, pocketed: false,
    }));
    const cue = bodies.find((b) => b.id === 0)!;
    cue.vx = Math.cos(angle) * power * MAX_SPEED;
    cue.vy = Math.sin(angle) * power * MAX_SPEED;
    const sim = simulate(bodies, POOL_TABLE, 0);
    for (const b of bodies) {
      const sb = s.balls.find((x) => x.id === b.id)!;
      sb.x = b.x; sb.y = b.y; sb.pocketed = b.pocketed;
    }
    s.shots++;
    ev.push({ type: "shot", seat, angle, power, frames: sim.frames, pocketed: sim.pocketed });

    const isBreak = !s.broken;
    s.broken = true;
    const myGroup = s.groups[seat];
    const potted = sim.pocketed.filter((id) => id !== 0);
    let foul: string | null = null;
    if (sim.pocketed.includes(0)) foul = "scratch";
    else if (sim.firstContact === null) foul = "no_contact";
    else if (!isBreak) {
      const fg = groupOf(sim.firstContact);
      const ownCleared = myGroup ? remaining({ ...s, balls: state.balls } as PoolState, myGroup) === 0 : false;
      if (myGroup && !ownCleared && fg !== myGroup) foul = "wrong_first_contact";
      if (myGroup && ownCleared && fg !== "eight") foul = "wrong_first_contact";
      if (!myGroup && fg === "eight") foul = "wrong_first_contact";
      if (!foul && potted.length === 0 && !sim.railAfterContact) foul = "no_rail";
    }

    if (potted.includes(8)) {
      const clearedBefore = myGroup
        ? state.balls.filter((b) => !b.pocketed && groupOf(b.id) === myGroup).length === 0
        : false;
      s.over = true;
      s.winner = !foul && clearedBefore && !isBreak ? seat : 1 - seat;
      ev.push({ type: "eight_potted", seat, winner: s.winner, foul });
      return { ok: true, state: s, events: ev };
    }

    if (!foul && !isBreak && !myGroup) {
      const firstPot = potted.find((id) => groupOf(id) !== "eight");
      if (firstPot !== undefined) {
        const g = groupOf(firstPot) as Group;
        s.groups[seat] = g;
        s.groups[1 - seat] = g === "solid" ? "stripe" : "solid";
        ev.push({ type: "groups_set", seat, group: g });
      }
    }
    const g = s.groups[seat];
    const keepTurn = !foul && (isBreak ? potted.length > 0 : g ? potted.some((id) => groupOf(id) === g) : false);
    s.lastFoul = foul;
    if (foul) {
      ev.push({ type: "foul", seat, foul });
      s.ballInHand = true;
      const c = s.balls.find((b) => b.id === 0)!;
      c.pocketed = true; // off the table until placed
    }
    if (!keepTurn) s.turn = 1 - seat;
    return { ok: true, state: s, events: ev };
  },

  view(s) {
    const { rng: _r, ...rest } = s;
    return clone(rest);
  },

  result(s) {
    if (!s.over || s.winner == null) return null;
    return {
      ranking: [[s.winner], [1 - s.winner]],
      scores: [0, 1].map((p) => (p === s.winner ? 1 : 0)),
      reason: "eight_ball",
    };
  },

  autoMove(s, seat) {
    if (s.over || s.turn !== seat) return null;
    if (s.ballInHand) {
      for (let x = HEAD_X; x > 20; x -= 25) for (let y = 250; y < 480; y += 25)
        if (freeSpot(s, x, y)) return { type: "place", x, y };
      return { type: "place", x: 60, y: 60 };
    }
    const cue = s.balls.find((b) => b.id === 0)!;
    const g = s.groups[seat];
    const targets = s.balls.filter((b) => !b.pocketed && b.id !== 0 &&
      (g ? (remaining(s, g) ? groupOf(b.id) === g : b.id === 8) : b.id !== 8));
    const t = targets.sort((a, b) => Math.hypot(a.x - cue.x, a.y - cue.y) - Math.hypot(b.x - cue.x, b.y - cue.y))[0];
    if (!t) return { type: "shoot", angle: 0, power: 0.5 };
    return { type: "shoot", angle: Math.atan2(t.y - cue.y, t.x - cue.x), power: 0.55 };
  },
};
